export interface Tag {
  id: string;
  label: string;
  icon: string;
}

export interface FilterTag extends Tag {
  type?: 'filter';
}

export interface CategoryTag extends Tag {
  type?: 'category';
}

// Types spécifiques pour les tags d'événements
export interface EventTag extends Tag {
  type?: 'event';
}

// Collection de tags prédéfinis
export const PREDEFINED_TAGS: Tag[] = [
  { id: 'free', label: 'Free', icon: '🆓' },
  { id: 'music', label: 'Music', icon: '🎵' },
  { id: 'sports', label: 'Sports', icon: '⚽' },
  { id: 'art', label: 'Art', icon: '🎨' },
  { id: 'food', label: 'Food', icon: '🍽️' },
  { id: 'culture', label: 'Culture', icon: '🏛️' },
  { id: 'yoga', label: 'Yoga', icon: '🧘' },
  { id: 'spirituality', label: 'Spirituality', icon: '🕉️' },
  { id: 'children', label: 'Children', icon: '👶' },
  { id: 'party', label: 'Party', icon: '🎉' },
];

// Couleurs pastel par tag pour le fond des cards sans image.
// Dérivées de la palette (variables.css) : teal, corail/terracotta,
// vieux rose (--color-blue-300), bleu acier (--color-blue-400), beiges.
export const TAG_FALLBACK_COLORS: Record<string, string> = {
  free: '#A9D3CB',         // teal clair
  music: '#F6AE8C',        // corail pastel
  sports: '#7FBFB4',       // teal
  art: '#A9C6D8',          // bleu acier pastel
  food: '#D9A188',         // terracotta pastel
  culture: '#DCC391',      // sable doré
  yoga: '#B5CBB0',         // vert sauge
  spirituality: '#C6A3A4', // vieux rose
  children: '#F0D9A0',     // jaune pâle
  party: '#F98F71',        // corail vif
};

// Beige du fond fallback actuel (--color-background-tertiary)
const FALLBACK_GRADIENT_BASE = '#e4d8c4';

/**
 * Trie les tags par ordre alphabétique (insensible à la casse),
 * pour un affichage et des combinaisons de couleurs stables.
 */
export function sortTags(tags?: string[]): string[] {
  return [...(tags ?? [])].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}

/**
 * Dégradé de fond pour les cards sans image, basé sur les tags de l'event
 * (triés alphabétiquement pour des combinaisons stables).
 * - 2 tags ou plus : dégradé de la couleur du tag 1 vers celle du tag 2
 * - 1 tag : dégradé de la couleur du tag vers le beige de fond
 * - Aucun tag connu : undefined (le fond beige du CSS s'applique)
 */
export function getTagFallbackGradient(tags?: string[]): string | undefined {
  const colors = sortTags(tags)
    .map(tag => TAG_FALLBACK_COLORS[tag.toLowerCase()])
    .filter((color): color is string => Boolean(color));

  if (colors.length === 0) return undefined;

  const [first, second = FALLBACK_GRADIENT_BASE] = colors;
  return `linear-gradient(135deg, ${first} 0%, ${second} 100%)`;
}